import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { createCopAdmin, fetchCopAdmin, fetchCopDashboard } from '../../services/copService';
import AdvancedConfiguration from '../super-admin/components/AdvancedConfiguration';
import AdminRecordTable from '../super-admin/components/AdminRecordTable';
import PageHeader from '../super-admin/components/PageHeader';
import WorkspaceNav from '../super-admin/components/WorkspaceNav';
import { safeAdminError } from '../super-admin/components/adminPresentation';

export const COP_ADMIN_SECTIONS = [
  ['dashboard', 'Dashboard'], ['organizations', 'Organizations'], ['configuration', 'Configuration'], ['relationshipDefinitions', 'Hierarchy Rules'], ['hierarchy', 'Hierarchy'],
  ['permissionDefinitions', 'Permissions'], ['roleDefinitions', 'Roles'], ['departmentDefinitions', 'Department Types'], ['departments', 'Departments'], ['members', 'Members'],
  ['roleAssignments', 'Role Assignments'], ['invitations', 'Invitations'], ['approvals', 'Approvals'], ['calendar', 'Calendar'], ['documentDefinitions', 'Document Types'],
  ['documents', 'Documents'], ['financeCategories', 'Finance Categories'], ['financeEntries', 'Finance'], ['budgets', 'Budgets'], ['invoices', 'Invoices'], ['sponsors', 'Sponsors'],
  ['sponsorPackages', 'Sponsor Packages'], ['sponsorContracts', 'Contracts'], ['volunteers', 'Volunteers'], ['grounds', 'Grounds'], ['groundBookings', 'Bookings'],
  ['maintenance', 'Maintenance'], ['trainingPrograms', 'Training Programs'], ['trainingBatches', 'Batches'], ['assessments', 'Assessments'], ['certificates', 'Certificates'],
  ['communications', 'Communications'], ['reports', 'Reports'], ['audit', 'Audit'],
];
const COP_GROUPS = [
  { label: 'Overview', items: COP_ADMIN_SECTIONS.filter(([key]) => ['dashboard', 'organizations', 'reports'].includes(key)) },
  { label: 'People', items: COP_ADMIN_SECTIONS.filter(([key]) => ['members', 'roleAssignments', 'invitations', 'approvals', 'volunteers'].includes(key)) },
  { label: 'Operations', items: COP_ADMIN_SECTIONS.filter(([key]) => ['calendar', 'departments', 'communications', 'documents', 'documentDefinitions'].includes(key)) },
  { label: 'Facilities', items: COP_ADMIN_SECTIONS.filter(([key]) => ['grounds', 'groundBookings', 'maintenance'].includes(key)) },
  { label: 'Training', items: COP_ADMIN_SECTIONS.filter(([key]) => ['trainingPrograms', 'trainingBatches', 'assessments', 'certificates'].includes(key)) },
  { label: 'Finance & sponsors', items: COP_ADMIN_SECTIONS.filter(([key]) => ['financeCategories', 'financeEntries', 'budgets', 'invoices', 'sponsors', 'sponsorPackages', 'sponsorContracts'].includes(key)) },
  { label: 'Governance', items: COP_ADMIN_SECTIONS.filter(([key]) => ['configuration', 'relationshipDefinitions', 'hierarchy', 'permissionDefinitions', 'roleDefinitions', 'departmentDefinitions', 'audit'].includes(key)) },
];
const READ_ONLY = new Set(['dashboard', 'membershipHistory', 'documentVersions', 'financeEntries', 'attendance', 'assessments', 'reports', 'audit']);

const CopAdminPage = () => {
  const { token } = useAuth();
  const [section, setSection] = useState('dashboard');
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('{}');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const title = useMemo(() => COP_ADMIN_SECTIONS.find(([key]) => key === section)?.[1], [section]);
  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const data = section === 'dashboard' ? await fetchCopDashboard(token) : await fetchCopAdmin(section, token);
      setItems(section === 'dashboard' ? [data] : data.items || []);
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load organization records.'));
    } finally {
      setBusy(false);
    }
  }, [section, token]);
  useEffect(() => { if (token) load(); }, [load, token]);
  const create = async () => {
    if (!reason.trim()) return setError('A change reason is required.');
    setBusy(true);
    setError('');
    try {
      await createCopAdmin(section, JSON.parse(draft), token, reason.trim());
      setDraft('{}');
      setReason('');
      await load();
    } catch (err) {
      setError(safeAdminError(err, 'Unable to save this organization record.'));
      setBusy(false);
    }
  };
  return (
    <section className="sa-workspace">
      <PageHeader title="Organization Platform" description="Manage cricket organization operations, members, facilities, training, finance records, and communications." />
      <WorkspaceNav groups={COP_GROUPS} value={section} onChange={setSection} ariaLabel="Organization Platform sections" />
      {error ? <div className="sa-error-state" role="alert"><strong>Organization Platform data could not be loaded.</strong><span>{error}</span></div> : null}
      <section className="sa-panel" role="tabpanel">
        <header className="sa-panel__header"><div><h2>{title}</h2><p>Canonical organization configuration and operational records.</p></div><button className="sa-secondary-button" type="button" onClick={load} disabled={busy}>Refresh</button></header>
        {busy ? <div className="sa-table-skeleton" aria-label="Loading organization records" /> : <AdminRecordTable items={items} />}
      </section>
      {section !== 'dashboard' && !READ_ONLY.has(section) ? <AdvancedConfiguration title="Advanced organization configuration" description="For reviewed operational definitions and records only." draft={draft} setDraft={setDraft} reason={reason} setReason={setReason} busy={busy} onCreate={create} textareaLabel="Organization Platform record JSON" /> : null}
    </section>
  );
};

export default CopAdminPage;
